'use client';

import React, { useEffect, useState } from 'react';
import ReviewCancellation from '@/components/manage-booking/reviewCancellation';
import SelectTrip from '@/components/manage-booking/selectTrip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { UserIcon } from 'lucide-react';
import {
  useGetCancellationChargesMutation,
  useGetPassengerDetailsMutation,
  useCancelBookingMutation,
} from '@flysync/shared-redux/services/booking'; // Ensure the path is correct
import { useSelector } from 'react-redux';
import { formatCurrency } from '@/lib/utils';
import { useRouter } from '@/lib/navigation';
import Image from '@/lib/app-image';
import { useToast } from '@/hooks/use-toast';
import { config, getTemplateAssetPath } from '@/config';
interface IPassengerDetails {
  origin: string;
  destination: string;
  travelDate: string;
  airlineName: string;
  airlineCode: string;
  passengers: {
    passengerId: number;
    name: string;
  }[];
}

function Cancellation() {
  const [getCancellationCharges, { isLoading, error: cancellationError }] =
    useGetCancellationChargesMutation();

  const [getPassengerDetails] = useGetPassengerDetailsMutation();

  const { toast } = useToast();

  const [cancelBooking] = useCancelBookingMutation();

  const [selectPax, setSelectPax] = useState<number[]>([]);

  const [isShowFare, setIsShowFare] = useState(false);

  const router = useRouter();

  const [cancellationCharges, setCancellationCharges] = useState(null);
  const [isApiCall, setIsApiCall] = useState(false);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const userData = useSelector((state: any) => state.user.stateData);

  const loader = getTemplateAssetPath('loader');

  useEffect(() => {
    if (userData?.booking_id) {
      if (!isApiCall && userData?.cancellationType == 'FR') {
        handleCancellationCharges();
        setIsShowFare(true);
        setIsApiCall(true);
      } else {
        setIsShowFare(false);
        handlePassengerDetails();
      }
    } else {
      router.push('/booking');
    }
  }, []);

  const handlePassengerDetails = async () => {
    try {
      const response: { status: { data: IPassengerDetails[] } } =
        await getPassengerDetails({
          data: {
            bookingId: userData?.booking_id,
          },
          auth: sessionStorage.getItem('token'),
        }).unwrap();
      setPassengerDetails(response?.status?.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectedPax = (pax: number[]) => {
    setSelectPax(pax);
  };

  const handleCancellationCharges = async () => {
    try {
      const response = await getCancellationCharges({
        data: {
          bookingId: userData?.booking_id,
          cancellationType: userData?.cancellationType,
          amendmentType: userData?.amendmentType,
          product_type: 'B2C',
          passenger: selectPax || [],
        },
        auth: sessionStorage.getItem('token'),
      }).unwrap();
      if (response?.data) {
        setCancellationCharges(response.data);
        setIsShowFare(true);
      } else {
        toast({
          title: 'Error',
          description: 'Cancellation charges not found',
          variant: 'error',
        });
        router.push('/booking');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleProceed = async () => {
    if (!selectPax?.length) {
      toast({
        title: 'Error',
        description: 'Please select at least one passenger',
        variant: 'error',
      });
    }
    if (cancellationCharges && isShowFare) {
      // TODO: Raise amendment request
      const response = await cancelBooking({
        data: {
          bookingId: userData?.booking_id,
          cancellationType: userData?.cancellationType,
          amendmentType: userData?.amendmentType,
          product_type: 'B2C',
          passenger: selectPax || [],
        },
        auth: sessionStorage.getItem('token'),
      }).unwrap();
      if (response?.status === 200) {
        toast({
          title: 'Cancellation request raised successfully',
          description: 'Please wait for the approval',
        });
      } else {
        toast({
          title: 'Cancellation request failed',
          description: 'Please try again',
        });
      }
      return;
    }
    if (
      selectPax.length > 0 &&
      userData?.cancellationType == 'CN' &&
      !isApiCall
    ) {
      handleCancellationCharges();
      setIsApiCall(true);
    }
  };

  return (
    <>
      {cancellationError && (
        <div>Error: {cancellationError?.data?.message}</div>
      )}
      {isLoading && (
        <div className="flex h-screen items-center justify-center">
          <Image src={loader} alt="loader" width={100} height={100} />
        </div>
      )}

      <div className="bg-tertiary">
        <div className="relative">
          <div className="h-14 w-full bg-primary sm:h-20 lg:h-[17rem]" />
          <div className="absolute inset-x-0 top-3 mx-auto px-1 pb-4 sm:top-6 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
            <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
              <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
                Review Cancellation
              </h2>
            </div>
            {/* Main Content Container */}
            <div className="flex flex-col gap-4 px-4 sm:gap-5 sm:px-6 lg:flex-row lg:gap-6 lg:px-0">
              {/* Left Section - Main Content */}
              <div className="order-2 w-full rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:order-1 lg:w-[75%] lg:p-8">
                {isShowFare && (
                  <ReviewCancellation fareData={cancellationCharges} />
                )}
                {!isShowFare && (
                  <SelectTrip
                    passengerDetails={passengerDetails}
                    setSelectedPax={handleSelectedPax}
                  />
                )}
              </div>

              {/* Right Section - Summary Card */}
              <div className="order-1 w-full lg:order-2 lg:w-[25%]">
                <Card className="rounded-xl border border-gray-200 bg-white shadow-lg sm:rounded-2xl">
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    {/* Flight Details */}
                    {passengerDetails.map((passenger, index) => (
                      <div key={index} className="space-y-2">
                        <div className="text-lg font-semibold text-gray-800 sm:text-xl lg:text-2xl">
                          {passenger?.origin} - {passenger?.destination}
                        </div>
                        <div className="text-xs text-gray-500 sm:text-sm">
                          {passenger?.travelDate}
                        </div>
                        <div className="mt-2 flex items-center gap-2 sm:mt-3">
                          <UserIcon className="h-4 text-gray-800 sm:h-5" />
                          <span className="text-xs font-medium text-gray-800 sm:text-sm">
                            {passenger?.passengers
                              .filter((pax) =>
                                selectPax.includes(pax.passengerId),
                              )
                              .map((pax) => pax.name)
                              .join(', ') || 'Select'}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Fare Details */}
                    {cancellationCharges && isShowFare && (
                      <>
                        <div className="mt-4 space-y-2 border-t border-gray-100 pt-4 sm:mt-5 sm:space-y-3 sm:pt-5">
                          {/* Total Paid */}
                          <div className="flex justify-between text-sm text-gray-800 sm:text-base">
                            <span>Total Paid</span>
                            <span className="font-medium">
                              {formatCurrency(
                                cancellationCharges?.data[0]?.totalAmount,
                              )}
                            </span>
                          </div>

                          {/* Deductions */}
                          <div className="flex justify-between text-sm text-gray-800 sm:text-base">
                            <span>Deductions</span>
                            <span className="font-medium text-red-600">
                              -
                              {formatCurrency(
                                cancellationCharges?.data[0]?.totalAmount -
                                  cancellationCharges?.data[0]
                                    ?.totalRefundableAmount,
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Refund Amount */}
                        <div className="my-4 border-y border-blue-200 py-3 sm:my-5 sm:py-4">
                          <div className="flex justify-between text-base font-semibold text-green-600 sm:text-lg">
                            <span>Your Refund</span>
                            <span>
                              {formatCurrency(
                                cancellationCharges?.data[0]
                                  ?.totalRefundableAmount,
                              )}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Proceed Button */}
                    <Button
                      onClick={handleProceed}
                      className="hover:bg-primary/90 mt-3 w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition-colors sm:mt-4 sm:py-3 sm:text-base"
                    >
                      Proceed
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Cancellation;
